import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import ApplicationGridItem, { Application } from "../ApplicationGridItem";
import FullscreenVideoModal from "../FullscreenVideoModal";

interface ChatUser {
  username: string;
  user_id: number;
  is_flat_member: boolean;
  profile_picture: string;
}

interface ApplicationChatCardProps {
  applicationText: string | null;
  isFlatMember: boolean;
  flatName: string;
  flatId: number;
  flatPicture: string;
  users: ChatUser[];
  applicationType?: "video" | "text";
  videoUrl?: string;
  createdAt?: string | null;
}

const ApplicationChatCard: React.FC<ApplicationChatCardProps> = ({
  applicationText,
  isFlatMember,
  flatName,
  flatId,
  flatPicture,
  users,
  applicationType = "text",
  videoUrl,
  createdAt,
}) => {
  const { getIdToken } = useAuth();
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | undefined>(
    undefined,
  );

  // Find the applicant (the user who is not a flat member)
  const applicant = users.find((u) => !u.is_flat_member);

  // Parse application text - remove prefix numbers (format: "id,index,actual_text")
  const parseApplicationText = (text: string | null): string | null => {
    if (!text) return null;
    const match = text.match(/^\d+,\d+,([\s\S]*)$/);
    return match ? match[1] : text;
  };

  // Fetch auth token for video playback
  useEffect(() => {
    const fetchToken = async () => {
      const token = await getIdToken();
      setAuthToken(token);
    };
    fetchToken();
  }, [getIdToken]);

  // Build an Application object from the chat props
  // flatPicture now contains the correct image URL for display
  // (flat image when user is applicant, applicant image when user is flat member)
  const application: Application = {
    application_id: 0, // Not needed for display
    user_id: applicant?.user_id || 0,
    room_id: 0, // Not needed for display
    type: applicationType,
    state: "sent", // Default state for chat view
    created_at: createdAt || new Date().toISOString(),
    text_application_answer: parseApplicationText(applicationText),
    url: videoUrl,
    // For sent applications (when user is NOT a flat member, they see flat info)
    flat_id: flatId,
    flat_name: flatName,
    flat_image: flatPicture, // Contains flat image when viewing as applicant
    // For received applications (when user IS a flat member, they see applicant info)
    applicant_name: flatName, // Use flatName which now has correct display name
    applicant_image: flatPicture, // Contains applicant image when viewing as flat member
  };

  const handleVideoClick = (url: string) => {
    setSelectedVideoUrl(url);
    setVideoModalOpen(true);
  };

  return (
    <>
      <div className="py-2">
        <div className="min-h-[384px] sm:min-h-[427px]">
          <ApplicationGridItem
            application={application}
            type={isFlatMember ? "received" : "sent"}
            onVideoClick={handleVideoClick}
          />
        </div>
      </div>

      {/* Video Modal */}
      <FullscreenVideoModal
        isOpen={videoModalOpen}
        videoUrl={selectedVideoUrl}
        onClose={() => setVideoModalOpen(false)}
        authToken={authToken}
      />
    </>
  );
};

export default ApplicationChatCard;
